/**
 * BookmarksList Component
 * Liste des bookmarks de l'utilisateur avec recherche et filtres
 */

"use client"

import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { Bookmark, ExternalLink, Search, Trash2, X } from "lucide-react"
import Link from "next/link"
import { useMemo, useState } from "react"
import { useBookmarks } from "../hooks/useBookmarks"
import type { Bookmark as BookmarkType } from "../types/bookmark.types"

interface BookmarksListProps {
  userEmail?: string
  className?: string
  onDelete?: (bookmarkId: string) => void
  emptyMessage?: string
}

export default function BookmarksList({
  userEmail,
  className,
  onDelete,
  emptyMessage = "You haven't saved any favourites yet.",
}: BookmarksListProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const { bookmarks, isLoading, error, refetch } = useBookmarks({
    userEmail,
    enabled: !!userEmail,
  })

  // Filtrer les bookmarks selon la recherche
  const filteredBookmarks = useMemo(() => {
    if (!searchQuery.trim()) return bookmarks

    const query = searchQuery.toLowerCase()
    return bookmarks.filter(
      (bookmark) =>
        bookmark.entityTitle.toLowerCase().includes(query) ||
        bookmark.metadata?.description?.toLowerCase().includes(query) ||
        bookmark.metadata?.category?.toLowerCase().includes(query)
    )
  }, [bookmarks, searchQuery])

  const handleDelete = async (bookmarkId: string) => {
    // The confirmation and toast are handled by the parent component
    onDelete?.(bookmarkId)
    // Refresh list after deletion
    setTimeout(() => refetch(), 300)
  }

  if (!userEmail) {
    return (
      <div className={cn("text-center py-8", className)}>
        <p className="text-muted-foreground">
          Please sign in to view your favourites.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={cn("space-y-4", className)}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="animate-pulse bg-muted rounded-lg h-24"
          />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className={cn("text-center py-8", className)}>
        <p className="text-destructive mb-4">
          Error loading favourites
        </p>
        <Button onClick={refetch} variant="outline" size="sm">
          Retry
        </Button>
      </div>
    )
  }

  if (bookmarks.length === 0) {
    return (
      <div className={cn("text-center py-12", className)}>
        <Bookmark className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search your favourites..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setSearchQuery("")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Counter */}
      <div className="text-sm text-muted-foreground">
        {filteredBookmarks.length === bookmarks.length
          ? `${bookmarks.length} favourite${bookmarks.length !== 1 ? "s" : ""}`
          : `${filteredBookmarks.length} of ${bookmarks.length} favourite${bookmarks.length !== 1 ? "s" : ""}`}
      </div>

      {/* Bookmarks list */}
      {filteredBookmarks.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            No favourites match your search.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredBookmarks.map((bookmark) => (
            <BookmarkItem
              key={bookmark.id}
              bookmark={bookmark}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function BookmarkItem({
  bookmark,
  onDelete,
}: {
  bookmark: BookmarkType
  onDelete: (id: string) => void
}) {
  const href = bookmark.metadata?.path || `/${bookmark.entityType}/${bookmark.entityId}`

  return (
    <div className="group relative bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-all duration-200">
      <div className="flex items-start gap-3">
        <Bookmark className="h-5 w-5 text-primary fill-current shrink-0 mt-0.5" />

        <div className="flex-1 min-w-0">
          <Link
            href={href}
            className="block hover:underline focus:outline-none focus:ring-2 focus:ring-primary rounded"
          >
            <h3 className="font-medium text-foreground line-clamp-2 mb-1">
              {bookmark.entityTitle}
            </h3>
          </Link>

          {bookmark.metadata?.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
              {bookmark.metadata.description}
            </p>
          )}

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {bookmark.metadata?.category && (
              <span className="inline-flex items-center px-2 py-1 rounded-md bg-muted">
                {bookmark.metadata.category}
              </span>
            )}
            <time dateTime={bookmark.created}>
              {new Date(bookmark.created).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </time>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            asChild
            className="h-8 w-8"
          >
            <Link href={href} aria-label={`View ${bookmark.entityTitle}`}>
              <ExternalLink className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(bookmark.id)}
            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
            aria-label={`Remove ${bookmark.entityTitle}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
